import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, tap } from 'rxjs';

import {
  NotificationActions,
  NavMenuIntercomActions,
  UsersStateActions,
  selectCurrentUser,
  selectCurrentUserId,
} from '@ninety/platform/state';
import { ConfirmDialogData, WarningConfirmDialogComponent } from '@ninety/ui/core';

import { MazActions } from '../../maz/_state/maz.actions';
import { Phase } from '../models/phase';
import { GettingStartedService } from '../services/getting-started.service';

import { GuideActions } from './guide.actions';
import { GuideSelectors } from './guide.selectors';

@Injectable()
export class GuideEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private gettingStartedService = inject(GettingStartedService);
  private dialog = inject(MatDialog);

  getGuideInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.init),
      switchMap(() =>
        this.gettingStartedService.getGuideInfo().pipe(map(guideInfo => GuideActions.setGuideInfo({ guideInfo })))
      )
    )
  );

  getPhases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.init, GuideActions.getPhasesAndCompletionPercentage),
      switchMap(() =>
        this.gettingStartedService.getPhases().pipe(
          map(({ phases, completion_percentage: completionPercentage }) =>
            GuideActions.setPhasesAndCompletionPercentage({
              phases: phases.map((phase, i) =>
                setPhaseComputedProperties(phase, phases[i - 1] && !phases[i - 1].completed)
              ),
              completionPercentage,
            })
          )
        )
      )
    )
  );

  /** this will be littered around the app at various places to check if the completion status changed */
  checkCompletionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.checkCompletionStatus),
      /** very important to only run this if the guide is enabled */
      concatLatestFrom(() => [
        this.store.select(GuideSelectors.selectIsGuideEnabled),
        this.store.select(GuideSelectors.selectCompletionPercentage),
      ]),
      filter(([_, enabled, completionPercentage]) => enabled && completionPercentage < 100),
      map(() => GuideActions.getPhasesAndCompletionPercentage())
    )
  );

  dismissGuide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.dismissGuide),
      switchMap(() =>
        this.dialog
          .open<WarningConfirmDialogComponent, ConfirmDialogData>(WarningConfirmDialogComponent, {
            data: {
              title: 'You did it!',
              message:
                "You've unlocked your company's full potential!  " +
                "You can still access the resources at <a href='https://ninety.io/library' target='_blank'>www.ninety.io/library</a>.  " +
                'Would you like to dismiss the onboarding guide permanently?',
              confirmButtonText: 'Dismiss',
              cancelButtonText: 'No',
            },
          })
          .afterClosed()
          .pipe(
            filter(result => result),
            map(() => GuideActions.dismissGuideConfirmed())
          )
      )
    )
  );

  dismissGuideConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.dismissGuideConfirmed),
      concatLatestFrom(() => this.store.select(selectCurrentUserId)),
      map(([_, userId]) => UsersStateActions.update({ userId, update: { guideDismissed: true } }))
    )
  );

  openResourceLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.openResourceLink),
      /** open the link in a new tab */
      tap(({ url }) => window.open(url, '_blank')),
      /** sometimes opening a link can trigger a completion status change */
      switchMap(({ url }) =>
        this.gettingStartedService.notifyResourceLinkOpened(url).pipe(map(() => GuideActions.checkCompletionStatus()))
      )
    )
  );

  somethingFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.somethingFailed, MazActions.failedToReceiveMessageFromMaz),
      map(e => NotificationActions.notifyError(e))
    )
  );

  /** Page Tracking */
  pageViewed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.pageViewed),
      concatLatestFrom(() => this.store.select(selectCurrentUser)),
      /** only track page views if the guide is not enabled and the user hasn't viewed the page before, second part is subject to change */
      filter(([{ page }, user]) => user?.guideEnabled && !user?.pageViews?.[page]),
      map(([{ page }, user]) => {
        const userId = user?._id;
        const pageViews = user?.pageViews ?? {};
        return UsersStateActions.update({ userId, update: { pageViews: { ...pageViews, [page]: 1 } } });
      })
    )
  );

  closeMazAndOpenIntercom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.closeMazAndOpenIntercom),
      map(() => NavMenuIntercomActions.showMessenger())
    )
  );
}

function setPhaseComputedProperties(phase: Phase, locked: boolean): Phase {
  const stepsCompleted = phase.steps.filter(s => s.completed).length;
  return {
    ...phase,
    locked,
    stepsCompleted,
    percentageComplete: (stepsCompleted / phase.steps.length) * 100,
  };
}
